import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { AttendanceSettingCreateVersionMutationVariables } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useForm } from 'modules/core/hooks/useForm'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EffectiveAtDisclaimerModal from 'modules/attendance/components/SettingItemPage/TimeTracking/EffectiveAtDisclaimerModal'
import { attendanceSettingVersionIndex } from 'modules/attendance/store/collections/settings'
import type AttendancePolicy from 'modules/attendance/store/models/AttendancePolicy'
import AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import { useCreateAttendanceVersion } from '../hooks/useCreateAttendanceVersion'

interface Props {
  attendancePolicy: AttendancePolicy
  attendanceSetting: AttendanceSetting
  attendanceSettings: AttendanceSetting[]
  onResponse: (newSetting: AttendanceSetting) => void
  onClose: () => void
}

const NewSettingVersionModal = ({
  attendancePolicy,
  attendanceSetting,
  attendanceSettings,
  onResponse,
  onClose,
}: Props) => {
  const [openEffectiveAtDisclaimerModal, setOpenEffectiveAtDisclaimerModal] = React.useState(false)

  const today = moment(new Date())
  const form = useForm<AttendanceSettingCreateVersionMutationVariables>({
    defaultValues: { id: attendanceSetting.id, effectiveAt: dateToString(today, 'backend') },
    onSubmit: async (data, { setErrors }) => {
      const result = await create({
        id: Number(data.id),
        effectiveAt: data.effectiveAt,
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)

        app.setFlash({
          type: 'wrong',
          message: errors?.effectiveAt || i18n.t('errors.generic_error'),
        })
      })

      if (handled.success && handled.data.employeesSetting) {
        const newSetting = AttendanceSetting.fromFragment(handled.data.employeesSetting)
        onResponse(newSetting)
        onClose()
      }

      return handled
    },
  })

  const { create, loading } = useCreateAttendanceVersion()

  const payload = {
    policy_name: attendancePolicy.attributes.name,
    counter: attendancePolicy.attributes.employees.length,
  }

  const onSubmit = () => {
    if (form.values.effectiveAt === dateToString(today, 'backend')) {
      form.handleSubmit()
    } else {
      setOpenEffectiveAtDisclaimerModal(true)
    }
  }

  const settingOptions: SelectOption[] = attendanceSettings.map((setting) => ({
    label: i18n.t('time_tracking.settings.versions.version_with_date', {
      index: attendanceSettingVersionIndex(setting),
      date: dateToString(setting.effectiveAt, 'ui'),
    }),
    value: setting.attributes.id,
  }))

  return (
    <>
      <Modal
        illustration="document-edit"
        title={i18n.t('time_tracking.settings.versions.add_modal.title')}
        description={i18n.t('time_tracking.settings.versions.add_modal.description', payload)}
        onClose={onClose}
        footer={
          <>
            <Button type="secondary" onClick={onClose}>
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={loading} onClick={onSubmit}>
              {i18n.t('time_tracking.settings.versions.add_modal.submit')}
            </Button>
          </>
        }
      >
        <Box gap="s16">
          <DayPicker
            label={i18n.t('time_tracking.settings.versions.effective_date')}
            value={form.values.effectiveAt}
            valueFormat="backend"
            onChange={(newDate: string) => form.setFieldValue('effectiveAt', newDate)}
          />
          <Select
            label={i18n.t('time_tracking.settings.versions.add_modal.based_on')}
            options={settingOptions}
            value={form.values.id.toString()}
            onChange={(id: string) => form.setFieldValue('id', id)}
          />
        </Box>
      </Modal>

      {openEffectiveAtDisclaimerModal && (
        <EffectiveAtDisclaimerModal
          onConfirm={form.handleSubmit}
          onClose={() => setOpenEffectiveAtDisclaimerModal(false)}
          newVersion
        />
      )}
    </>
  )
}

export default observer(NewSettingVersionModal)
